/* =============================================================================
   alertToasts.js (TB-191)
   In-app alerts: a toast (not a modal) for each new unread notification, so
   they show while the app is open even when push permission is off.

   Feed: GET transactions my-notifications/latest?since=<serverTime of the last
   call> returns up to 5 unread items newer than since (+ total). useNavCounts
   calls checkLatestAlerts() when the unread count rises (polled every 20 s)
   and on a foreground FCM message. Nothing is marked seen by showing a toast.

   Rules: max 3 visible, the rest queue as "+N more"; 6 s auto-hide that
   pauses on hover and focus; never on the notifications page; one toast per
   notification id; no toast for a notification the FCM card already showed
   (handleFcm), and no FCM card when a toast already showed it.
   ========================================================================== */
import { reactive } from 'vue'
import axios from 'axios'
import emitter from '@/event-bus.js'
import { readableDates } from '@/utils/notificationsApi'

export const MAX_VISIBLE = 3
export const HIDE_MS = 6000
const FCM_WINDOW_MS = 20000
const NOTIFICATION_PATHS = ['/notifications', '/doctor/notifications']

export const alerts = reactive({ items: [], more: 0 })

let cursor = null
let busy = false
let generation = 0
let seq = 0
const known = new Set() // notification ids already toasted or covered by the FCM card
const queue = []
const timers = {}
const fcmSeen = [] // { title, at }
const toasted = [] // { title, at }

function tx () { return process.env.VUE_APP_TRANSACTIONS_SERVICE_API || '' }
function headers () {
  try {
    const t = localStorage.getItem('accessToken')
    return t ? { Authorization: `Bearer ${t}` } : null
  } catch (e) { return null }
}
function storedRole () {
  try {
    const u = JSON.parse(localStorage.getItem('user')) || {}
    return u.userRole || u.role || ''
  } catch (e) { return '' }
}
function currentPath () {
  try { return window.location.pathname || '' } catch (e) { return '' }
}
export function onNotificationsPage () {
  const p = currentPath()
  return NOTIFICATION_PATHS.some((n) => p === n || p.startsWith(`${n}/`))
}

const PAYMENT = ['pending-payment', 'invoice']
const VISIT = ['pre-appointment', 'post-appointment', 'notify', 'invitation']

export function kindOf (n) {
  if (!n) return 'bell'
  if (n.kind) return n.kind
  if (n.type === 'reminder') return 'reminder'
  if (PAYMENT.includes(n.type)) return 'payment'
  if (VISIT.includes(n.type) || (n.appointment && n.appointment.id)) return 'visit'
  return 'bell'
}

/** Where tapping a notification goes: the same targets the notifications page links to. */
export function routeFor (n, role = storedRole()) {
  const doctor = role === 'provider' || role === 'establishment'
  if (n.to) return n.to
  if (n.type === 'invoice' && n.appointment && n.appointment.invoice && n.appointment.invoice.id) {
    return doctor ? `/doctor/invoice/${n.appointment.invoice.id}` : `/invoice/${n.appointment.invoice.id}`
  }
  if (n.type === 'invitation') {
    return role === 'establishment' ? { path: '/doctor/establishment-doctor', query: { view: 'doctors' } } : '/doctor/clinics'
  }
  if (n.appointment && n.appointment.id) return `/appointment-details/${n.appointment.id}`
  if (n.type === 'reminder' && !doctor) return { path: '/reminders', query: { tab: 'mine' } }
  return doctor ? '/doctor/notifications' : '/notifications'
}

function clearTimer (id) {
  if (timers[id]) { clearTimeout(timers[id].t); delete timers[id] }
}
function arm (id, ms) {
  clearTimer(id)
  timers[id] = { t: setTimeout(() => dismissAlert(id), ms), at: Date.now(), left: ms }
}

function promote () {
  while (alerts.items.length < MAX_VISIBLE && queue.length) show(queue.shift())
  alerts.more = queue.length
}

function show (a) {
  alerts.items.push(a)
  toasted.push({ title: a.title, at: Date.now() })
  arm(a.id, HIDE_MS)
}

export function dismissAlert (id) {
  clearTimer(id)
  const i = alerts.items.findIndex((a) => a.id === id)
  if (i >= 0) alerts.items.splice(i, 1)
  promote()
}
export function pauseAlert (id) {
  const t = timers[id]
  if (!t) return
  clearTimeout(t.t)
  t.left = Math.max(1500, t.left - (Date.now() - t.at))
  t.t = null
}
export function resumeAlert (id) {
  const t = timers[id]
  if (!t || t.t) return
  arm(id, t.left)
}

/** Shows one toast. `id` dedupes; returns false when it was skipped. */
export function pushAlert (a) {
  if (!a || !a.id || known.has(a.id)) return false
  known.add(a.id)
  if (known.size > 500) known.delete(known.values().next().value)
  if (onNotificationsPage()) return false
  const item = { id: a.id, kind: kindOf(a), title: a.title || 'Notification', message: a.message || '', to: routeFor(a), notificationId: a.notificationId || null }
  if (alerts.items.length < MAX_VISIBLE) show(item)
  else { queue.push(item); alerts.more = queue.length }
  return true
}

/** Tap on a toast: marks that notification read, then goes where it points. */
export function openAlert (a, router) {
  dismissAlert(a.id)
  const h = headers()
  if (a.notificationId && h) {
    axios.patch(`${tx()}my-notifications/seen`, { ids: [a.notificationId] }, { headers: h })
      .then(() => { try { emitter.emit('navCountsRefresh') } catch (e) { /* no listener */ } }, () => {})
  }
  if (router && a.to) router.push(a.to).catch(() => {})
}

function recent (list, title) {
  const now = Date.now()
  return list.some((x) => x.title === title && now - x.at < FCM_WINDOW_MS)
}

/**
 * Foreground FCM message. Returns true when a toast already showed the same
 * notification (the caller skips its own card); otherwise remembers it so the
 * toast feed does not show it a second time.
 */
export function handleFcm (payload) {
  const title = (payload && payload.notification && payload.notification.title) || (payload && payload.data && payload.data.title) || ''
  if (!title) return false
  if (recent(toasted, title)) return true
  fcmSeen.push({ title, at: Date.now() })
  if (fcmSeen.length > 20) fcmSeen.shift()
  return false
}

/** Starts the cursor at the server's clock so only later notifications toast. */
export async function primeAlerts () {
  const h = headers()
  if (!h || cursor || !tx()) return
  const gen = generation
  try {
    const res = await axios.get(`${tx()}my-notifications/latest`, { headers: h, timeout: 15000 })
    if (gen === generation && !cursor) cursor = res.data.data.serverTime
  } catch (e) { /* the first rise primes it */ }
}

export function resetAlerts () {
  generation += 1
  cursor = null
  busy = false
  Object.keys(timers).forEach(clearTimer)
  alerts.items.splice(0)
  queue.splice(0)
  alerts.more = 0
  known.clear()
  fcmSeen.splice(0)
  toasted.splice(0)
}

export async function checkLatestAlerts () {
  const h = headers()
  if (!h || !tx() || busy) return
  if (!cursor) { await primeAlerts(); return }
  busy = true
  const gen = generation
  try {
    const res = await axios.get(`${tx()}my-notifications/latest`, { headers: h, params: { since: cursor }, timeout: 15000 })
    if (gen !== generation) return
    const data = (res && res.data && res.data.data) || {}
    const items = Array.isArray(data.items) ? data.items : []
    // oldest first so the newest ends on top
    items.slice().reverse().forEach((n) => {
      const covered = recent(fcmSeen, n.title)
      if (covered) {
        known.add(`n:${n.id}`)
        const i = fcmSeen.findIndex((x) => x.title === n.title)
        if (i >= 0) fcmSeen.splice(i, 1)
        return
      }
      pushAlert({ id: `n:${n.id}`, notificationId: n.id, type: n.type, appointment: n.appointment, title: n.title, message: readableDates(n.description || '') })
    })
    const extra = (Number(data.total) || 0) - items.length
    if (extra > 0 && !onNotificationsPage()) {
      pushAlert({ id: `more:${++seq}`, kind: 'bell', title: `+${extra} more`, message: 'Open your notifications to see them all.', to: routeFor({ type: 'notify' }) })
    }
    if (data.serverTime) cursor = data.serverTime
  } catch (e) {
    /* keep the cursor; the next rise asks again */
  } finally {
    busy = false
  }
}
